import React, { useState, useEffect } from 'react'
import { Container, Box, Typography } from '@mui/material';
import UserProfileLeftSidebar from './UserProfileLeftSidebar';
import UserRightSidebar from './user-right-side/UserRightSidebar';
import { fetchUserData } from '../../api/user';
import UserProfileSkeleton from '../user-management/UserProfileSkeleton';

// `isDeletedAccount` is now a HINT, not a requirement. Only two call sites can
// know it up front (the Deleted Accounts list and Profile Activations, which
// both hold a delete-log row); every other screen — Member Engagement,
// Subscription Report, Messaging Activity, Payment Analytics, Online Now,
// campaign and user lists — opens a profile by user id without knowing whether
// that account still exists. `GET /admin/user/:id` now falls back to the delete
// log on its own and flags the response, so this component reads the flag off
// the payload and renders every deleted member read-only wherever it is mounted.
const UserProfileMainPage = ({ userId,isDeletedAccount=false }) => {
  const [userData, setUserData] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetchData = async () => {
    try {
      // isAbort:false — getRequest keys in-flight GETs on the path, so a second
      // component reading the SAME user id (the chat-initiate dialog does)
      // cancelled this one and left the modal stuck on the skeleton forever.
      // With aborts off, "finished loading and still no user" unambiguously
      // means not found, which is what the empty state below reports.
      await fetchUserData(userId, setUserData, isDeletedAccount, undefined, { isAbort: false });
    } catch (error) {
      console.error('Error while loading real-time data', error);
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return <UserProfileSkeleton />;
  }
  // Previously this shared the skeleton branch, so a profile that failed to load
  // spun on a loading placeholder forever with no explanation — the reported symptom.
  if (!userData) {
    return (
      <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#6A513E' }}>
          This profile could not be loaded
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#5F6368' }}>
          The account may have been permanently removed.
        </Typography>
      </Box>
    );
  }
  // The server's flag wins where it is set; the prop still covers the deleted
  // endpoint's callers.
  const deleted = isDeletedAccount || !!userData?.isDeletedAccount;
  return (
    <Box sx={{ backgroundColor: { xs: '#fff', sm: '#F9F6F3' } }}>
      <Container maxWidth="lg" sx={{ padding: { xs: '0px 0px 0 0px', sm: '15px 15px' } }}>
        {deleted && (
          <Box sx={{ bgcolor: '#fdecea', color: '#cf4233', borderRadius: 2, px: 2, py: 1.25, mb: 2, fontSize: '14px', fontWeight: 500 }}>
            This member deleted their account — the profile below is the archived copy and is read-only.
          </Box>
        )}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} sx={{ alignItems: { xs: 'center', sm: 'center', md: 'flex-start', }, }}>
          <UserProfileLeftSidebar userData={userData} isDeletedAccount={deleted}/>
          <UserRightSidebar userData={userData} isDeletedAccount={deleted}/>
        </Box>
      </Container>
    </Box>
  )
}

export default UserProfileMainPage
